import { gql } from '@apollo/client';

// User mutations
export const CREATE_USER = gql`
  mutation CreateUser($input: UserCreateInput!) {
    createUser(input: $input) {
      _id
      email
      name
      role
      department
      designation
      specialization
      isActive
    }
  }
`;

export const UPDATE_USER = gql`
  mutation UpdateUser($id: ID!, $input: UserUpdateInput!) {
    updateUser(id: $id, input: $input) {
      _id
      email
      name
      role
      department
      designation
      specialization
      isActive
    }
  }
`;

export const DELETE_USER = gql`
  mutation DeleteUser($id: ID!) {
    deleteUser(id: $id)
  }
`;

// Module mutations
export const CREATE_MODULE = gql`
  mutation CreateModule($input: ModuleCreateInput!) {
    createModule(input: $input) {
      _id
      id
      title
      description
      duration
      difficulty
      icon
      category
      tags
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
      content {
        sections {
          title
          content
          video {
            src
            title
            caption
          }
        }
      }
      quiz {
        question
        options
        correct
      }
    }
  }
`;

export const UPDATE_MODULE = gql`
  mutation UpdateModule($id: String!, $input: ModuleUpdateInput!) {
    updateModule(id: $id, input: $input) {
      _id
      id
      title
      description
      duration
      difficulty
      icon
      category
      tags
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
      content {
        sections {
          title
          content
          video {
            src
            title
            caption
          }
        }
      }
      quiz {
        question
        options
        correct
      }
    }
  }
`;

export const DELETE_MODULE = gql`
  mutation DeleteModule($id: String!) {
    deleteModule(id: $id)
  }
`;

// Notification mutations
export const CREATE_NOTIFICATION = gql`
  mutation CreateNotification($input: NotificationCreateInput!) {
    createNotification(input: $input) {
      _id
      title
      message
      type
      read
      moduleId
      createdAt
    }
  }
`;

export const MARK_NOTIFICATION_AS_READ = gql`
  mutation MarkNotificationAsRead($id: ID!) {
    markNotificationAsRead(id: $id) {
      _id
      read
    }
  }
`;

export const MARK_ALL_NOTIFICATIONS_AS_READ = gql`
  mutation MarkAllNotificationsAsRead($userEmail: String!) {
    markAllNotificationsAsRead(userEmail: $userEmail)
  }
`;

export const DELETE_NOTIFICATION = gql`
  mutation DeleteNotification($id: ID!) {
    deleteNotification(id: $id)
  }
`;

export const CLEAR_ALL_NOTIFICATIONS = gql`
  mutation ClearAllNotifications($userEmail: String!) {
    clearAllNotifications(userEmail: $userEmail)
  }
`;

// Progress mutations
export const CREATE_OR_UPDATE_PROGRESS = gql`
  mutation CreateOrUpdateProgress($input: ProgressInput!) {
    createOrUpdateProgress(input: $input) {
      _id
      completed
      completedAt
      quizCompleted
      quizScore
      quizCompletedAt
      timeSpent
      currentSection
      selectedAnswers
    }
  }
`;

export const UPDATE_PROGRESS = gql`
  mutation UpdateProgress($userEmail: String!, $moduleId: String!, $input: ProgressInput!) {
    updateProgress(userEmail: $userEmail, moduleId: $moduleId, input: $input) {
      _id
      completed
      completedAt
      quizCompleted
      quizScore
      quizCompletedAt
      timeSpent
      currentSection
      selectedAnswers
    }
  }
`;

// Auth mutations
export const LOGIN = gql`
  mutation Login($input: LoginInput!) {
    login(input: $input) {
      success
      user {
        _id
        email
        name
        role
        department
        designation
        specialization
      }
      error
    }
  }
`;

// Schedule mutations
export const CREATE_SCHEDULE = gql`
  mutation CreateSchedule($input: ScheduleCreateInput!) {
    createSchedule(input: $input) {
      _id
      id
      moduleId
      title
      description
      startDate
      endDate
      startTime
      endTime
      location
      instructor
      maxParticipants
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
    }
  }
`;

export const UPDATE_SCHEDULE = gql`
  mutation UpdateSchedule($id: String!, $input: ScheduleUpdateInput!) {
    updateSchedule(id: $id, input: $input) {
      _id
      id
      moduleId
      title
      description
      startDate
      endDate
      startTime
      endTime
      location
      instructor
      maxParticipants
      assignments {
        roles
        users
        departments
        designations
        specializations
      }
    }
  }
`;

export const DELETE_SCHEDULE = gql`
  mutation DeleteSchedule($id: String!) {
    deleteSchedule(id: $id)
  }
`;

// Prescreening mutations
export const CREATE_OR_UPDATE_PRESCREENING = gql`
  mutation CreateOrUpdatePrescreening($input: PrescreeningInput!) {
    createOrUpdatePrescreening(input: $input) {
      _id
      userEmail
      category
      level
      profession
      goal
      tags
      completed
      skipped
    }
  }
`;

export const DELETE_PRESCREENING = gql`
  mutation DeletePrescreening($userEmail: String!) {
    deletePrescreening(userEmail: $userEmail)
  }
`;

// Settings mutations
export const CREATE_OR_UPDATE_SETTING = gql`
  mutation CreateOrUpdateSetting($input: SettingsInput!) {
    createOrUpdateSetting(input: $input) {
      _id
      key
      value
      description
      category
    }
  }
`;

export const DELETE_SETTING = gql`
  mutation DeleteSetting($key: String!) {
    deleteSetting(key: $key)
  }
`;

// Tag mutations
export const CREATE_TAG = gql`
  mutation CreateTag($input: TagCreateInput!) {
    createTag(input: $input) {
      _id
      name
      displayName
      description
      category
      isActive
      order
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_TAG = gql`
  mutation UpdateTag($name: String!, $input: TagUpdateInput!) {
    updateTag(name: $name, input: $input) {
      _id
      name
      displayName
      description
      category
      isActive
      order
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_TAG = gql`
  mutation DeleteTag($name: String!) {
    deleteTag(name: $name)
  }
`;

// Role mutations
export const CREATE_ROLE = gql`
  mutation CreateRole($input: RoleCreateInput!) {
    createRole(input: $input) {
      _id
      name
      displayName
      description
      isActive
      order
      permissions
      createdAt
      updatedAt
    }
  }
`;

export const UPDATE_ROLE = gql`
  mutation UpdateRole($name: String!, $input: RoleUpdateInput!) {
    updateRole(name: $name, input: $input) {
      _id
      name
      displayName
      description
      isActive
      order
      permissions
      createdAt
      updatedAt
    }
  }
`;

export const DELETE_ROLE = gql`
  mutation DeleteRole($name: String!) {
    deleteRole(name: $name)
  }
`;

